import { useEffect, useMemo, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { useTileCache } from "../hooks/useTileCache";

export default function CachedTileLayer({
  url,
  maxZoom = 19,
  attribution,
  projectId = null,
  paftaIds = [],
}) {
  const map = useMap();
  const { getTileSource } = useTileCache();
  const normalizedPaftaIds = useMemo(
    () => [...new Set((paftaIds || []).map((id) => String(id || "").trim()).filter(Boolean))],
    [paftaIds]
  );
  const projectIdRef = useRef(projectId);
  const paftaIdsRef = useRef(normalizedPaftaIds);

  useEffect(() => {
    projectIdRef.current = projectId;
    paftaIdsRef.current = normalizedPaftaIds;
  }, [projectId, normalizedPaftaIds]);

  useEffect(() => {
    const OfflineTileLayer = L.TileLayer.extend({
      createTile: function createTile(coords, done) {
        const tile = document.createElement("img");

        L.DomEvent.on(tile, "load", L.Util.bind(this._tileOnLoad, this, done, tile));
        L.DomEvent.on(tile, "error", L.Util.bind(this._tileOnError, this, done, tile));

        if (this.options.crossOrigin || this.options.crossOrigin === "") {
          tile.crossOrigin =
            this.options.crossOrigin === true ? "" : this.options.crossOrigin;
        }

        tile.alt = "";
        tile.setAttribute("role", "presentation");

        const tileUrl = this.getTileUrl(coords);
        getTileSource(tileUrl, projectIdRef.current, paftaIdsRef.current)
          .then((resolvedSource) => {
            tile.src = resolvedSource || tileUrl;
          })
          .catch(() => {
            tile.src = tileUrl;
          });

        return tile;
      },
    });

    const layer = new OfflineTileLayer(url, { maxZoom, attribution });
    layer.addTo(map);

    return () => {
      map.removeLayer(layer);
    };
  }, [attribution, getTileSource, map, maxZoom, url]);

  return null;
}
